import React, { useState, useEffect, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import SchoolIcon from '@mui/icons-material/School';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { apiUrl, fetchJson } from '../apiBase';

// Simple in-memory caches
const IS_PROD = process.env.NODE_ENV === 'production';
const STATS_TTL_MS = 1500; // dev-only TTL to smooth remounts
const statsInFlight = new Map(); // key -> Promise<{ok, data}>
const statsResolved = new Map(); // key -> { data, expiresAt }

const StatCard = ({ title, value, icon: Icon, color }) => {
  
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        position: 'relative',
        background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 25px ${alpha(color, 0.15)}`
        }
      }}
    >
      <Box sx={{ pr: 6 }}>
        {value !== '' && value !== null && value !== undefined && (
          <Typography
            variant="h2"
            component="div"
            sx={{
              fontWeight: 800,
              color: color,
              mb: 0.5,
              lineHeight: 1,
              fontSize: { xs: 34, sm: 40, md: 44 }
            }}
          >
            {value}
          </Typography>
        )}
        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          backgroundColor: alpha(color, 0.15),
          borderRadius: '50%',
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Icon sx={{ fontSize: 24, color: color }} />
      </Box>
    </Box>
  );
};

const DashboardStats = ({ onError, schoolId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const key = schoolId ? `school:${schoolId}` : 'global';

      // 1) Serve from short-lived resolved cache if valid (dev only)
      if (!IS_PROD) {
        const cached = statsResolved.get(key);
        if (cached && cached.expiresAt > Date.now()) {
          setStats({ ...cached.data, school_name: schoolId });
          return;
        } else if (cached) {
          statsResolved.delete(key);
        }
      }

      // 2) Coalesce in-flight requests
      let promise = statsInFlight.get(key);
      if (!promise) {
        const url = schoolId
          ? apiUrl(`/admin/stats/${encodeURIComponent(schoolId)}`)
          : apiUrl('/admin/stats');
        promise = (async () => {
          try {
            const response = await fetchJson(url);
            if (!response.ok) return { ok: false };
            const data = await response.json();
            return { ok: true, data };
          } catch (_) {
            return { ok: false };
          }
        })();
        statsInFlight.set(key, promise);
      }

      const result = await promise;
      statsInFlight.delete(key);

      if (result.ok) {
        // populate short TTL cache in dev; prod remains in-flight only
        if (!IS_PROD) {
          statsResolved.set(key, { data: result.data, expiresAt: Date.now() + STATS_TTL_MS });
        }
        setStats({ ...result.data, school_name: schoolId });
      } else {
        onError('İstatistikler yüklenemedi');
      }
    } catch (err) {
      onError('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [schoolId, onError]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <Grid container spacing={4}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Box sx={{ p: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
              <Skeleton variant="text" width={80} height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={120} />
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!stats) {
    return null;
  }

  const statItems = [
    {
      title: 'Toplam Öğrenci',
      value: stats.students,
      icon: SchoolIcon,
      color: theme.palette.primary.main
    },
    {
      title: 'Toplam Veli',
      value: stats.parents,
      icon: GroupIcon,
      color: theme.palette.success.main
    },
    (() => {
      const empty = !stats.exam_results;
      return {
        title: empty ? 'Henüz sınav sonucu yok' : 'Sınav Sonuçları',
        value: empty ? '' : stats.exam_results,
        icon: AssignmentIcon,
        color: theme.palette.warning.main,
      };
    })(),
    {
      title: 'Aktif Sistem',
  value: stats.active_system ? '✓' : '-',
      icon: CheckCircleIcon,
      color: theme.palette.info.main
    }
  ];

  return (
    <Grid container spacing={4}>
      {statItems.map((item, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <StatCard {...item} />
        </Grid>
      ))}
    </Grid>
  );
};

export default DashboardStats;
